
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from './icons';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (e?: React.MouseEvent | React.FormEvent) => void | Promise<void>;
  title: string;
  message: string | React.ReactNode;
  confirmButtonText?: string;
  confirmButtonClass?: string;
  cancelButtonText?: string;
  icon?: React.ElementType<{ className?: string }>;
  iconClassName?: string;
  iconBgClassName?: string;
  isLoading?: boolean;
  hideCancel?: boolean;
  zIndex?: number;
  hideMessage?: boolean;
  customBody?: React.ReactNode;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  confirmButtonClass = "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  icon: Icon = ExclamationTriangleIcon,
  iconClassName = "text-amber-600",
  iconBgClassName = "bg-amber-100",
  isLoading = false,
  hideCancel = false,
  zIndex = 50,
  hideMessage = false,
  customBody
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);

  useFocusTrap(modalRef, isOpen, undefined, onClose);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      style={{ zIndex }}
    >
      <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative transform transition-all scale-100" role="dialog" aria-modal="true" aria-labelledby={title ? "modal-title" : undefined}>
        <div className="flex items-start space-x-4">
          {Icon && (
              <div className={`flex-shrink-0 p-3 rounded-full ${iconBgClassName}`}>
                  <Icon className={`w-6 h-6 ${iconClassName}`} />
              </div>
          )}
          <div className="flex-1 w-full relative">
            {title && <h2 id="modal-title" className="text-lg font-bold text-gray-900 mb-2">{title}</h2>}
            {!hideMessage && (
              <div className="text-sm text-gray-500 leading-relaxed">
                {message}
              </div>
            )}
            {customBody && <div className="mt-4">{customBody}</div>}
          </div>
        </div>

        <div className={`mt-8 flex gap-3 ${Icon ? 'ml-0 sm:ml-16' : ''} flex-wrap sm:flex-nowrap justify-end sm:justify-start`}>
          {!hideCancel && (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors"
            >
              {cancelButtonText || t('modals.cancel')}
            </button>
          )}
          <button
            disabled={isLoading}
            onClick={onConfirm}
            className={`flex-1 px-4 py-2 text-white rounded-lg font-medium whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-md transition-all transform hover:-translate-y-0.5 ${confirmButtonClass} ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.processing') || 'Verarbeite...'}
              </span>
            ) : (
              confirmButtonText || t('modals.confirmDelete') || 'Bestätigen'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
